import React, { useState, useEffect } from 'react';
import { Briefcase, Wallet, CheckCircle, Shield, Loader2, PlayCircle } from 'lucide-react';

export default function MitarbeiterDashboard() {
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => { fetchStats(); }, []);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('tester_token');
      const response = await fetch('/api/employee/stats', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) setStats(await response.json());
    } catch (err) { console.error(err); } finally { setIsLoading(false); }
  };

  if (isLoading || !stats) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] text-secondary">
        <Loader2 className="w-8 h-8 animate-spin mb-4 text-accent" />
        <p>Lade persönliche Daten...</p>
      </div>
    );
  }

  const dashboardStats = [
    { title: 'Offene Aufträge', value: stats.open_jobs.toString(), icon: Briefcase, color: 'text-accent', bg: 'bg-sky-50' },
    { title: 'Verdientes Guthaben', value: `${stats.balance.toFixed(2).replace('.', ',')} €`, icon: Wallet, color: 'text-green-500', bg: 'bg-green-50' },
    { title: 'Erfolgreiche Tests', value: stats.completed_tests.toString(), icon: CheckCircle, color: 'text-indigo-500', bg: 'bg-indigo-50' },
    { title: 'Prüf-Status', value: stats.verification_status === 'verified' ? 'Akkreditiert' : 'Ausstehend', icon: Shield, color: stats.verification_status === 'verified' ? 'text-emerald-500' : 'text-amber-500', bg: stats.verification_status === 'verified' ? 'bg-emerald-50' : 'bg-amber-50' },
  ];

  return (
    <div className="animate-in fade-in slide-in-from-bottom-4">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-primary">Willkommen im Netzwerk, {stats.first_name}!</h1>
        <p className="text-muted-foreground">Tester-ID: {stats.tester_id} | Prüfen Sie Anwendungen unter echten Bedingungen.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {dashboardStats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <div key={index} className="bg-white p-6 rounded-xl border border-border shadow-sm flex items-center gap-4">
              <div className={`p-4 rounded-lg ${stat.bg}`}>
                <Icon className={`w-6 h-6 ${stat.color}`} />
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">{stat.title}</p>
                <h3 className="text-2xl font-bold text-primary">{stat.value}</h3>
              </div>
            </div>
          );
        })}
      </div>

      <div className="bg-white rounded-xl border border-border shadow-sm overflow-hidden mb-8">
        <div className="px-6 py-4 border-b border-border bg-slate-50/50 flex justify-between items-center">
          <h2 className="font-semibold text-primary">Neu zugewiesene Aufträge</h2>
          <span className="text-xs bg-accent/20 text-primary font-bold px-2.5 py-1 rounded-full">Minijob-geeignet</span>
        </div>
        <div className="p-0">
          {stats.available_jobs.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              Aktuell sind keine neuen Test-Szenarien verfügbar.
            </div>
          ) : (
            <div className="space-y-0 divide-y divide-border">
              {stats.available_jobs.map((test) => (
                <div key={test.id} className="flex flex-col sm:flex-row sm:items-center justify-between p-6 hover:bg-slate-50 transition-colors gap-4">
                  <div>
                    <span className="text-xs font-mono text-muted-foreground">{test.id}</span>
                    <h3 className="font-bold text-primary text-lg">{test.title}</h3>
                    <p className="text-sm text-muted-foreground">Fokus: {test.type} • Geschätzte Zeit: {test.duration}</p>
                  </div>
                  <div className="flex items-center justify-between sm:justify-end gap-6">
                    <div className="text-left sm:text-right">
                      <p className="text-xs text-muted-foreground">Vergütung</p>
                      <p className="font-bold text-green-600 text-lg">{test.reward}</p>
                    </div>
                    
                    <a href="/mitarbeiter/auftrage" className="bg-accent text-white px-5 py-2.5 rounded-md font-medium flex items-center gap-2 text-sm hover:bg-accent/90 shadow-sm transition-colors">
                      <PlayCircle className="w-4 h-4" /> Jetzt annehmen
                    </a>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}